'use client';

import { c as _c } from "react/compiler-runtime";
import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { Button, Chip } from '@common/components';
import { cn } from '@core/utils';
import { useQueryErrorResetBoundary, useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useDeferredValue, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { GiftCard } from '@/app/_components/GiftCard/GiftCard';
import { CategoryGroupRows } from '@/app/donation/_components/CategoryGroupRows';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import { HorizontalScroll } from '@/components/client';
import { Responsive } from '@/components/organism/Responsive';
import { BREAKPOINTS, DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { CategoryGroupGiftsData } from './CategoryGroupGiftsSectionServer';
import { CategoryGroupItem } from './CategoryGroupItem';
import { ALL_CATEGORY_GROUP_CODE, GIFT_PAGE_SIZE, GIFT_PRODUCT_SORT_TYPE, type GiftCategoryGroupCode, type GiftProductSortType, MOBILE_GIFT_COUNT, SORT_TYPE_OPTIONS } from './constants';
const GIFT_LIST_CLASS_NAME = cn('grid w-full grid-cols-4 gap-x-20 gap-y-40 pb-60', 'mobile:grid-cols-2 tablet:grid-cols-3 mobile-tablet:gap-x-16 mobile-tablet:gap-y-32 mobile-tablet:pb-40');
interface CategoryGroupGiftListProps {
  groupCode: GiftCategoryGroupCode;
  sortType: GiftProductSortType;
  initialGiftsResponse: CategoryGroupGiftsData['initialGiftsResponse'];
  entry: CategoryGroupGiftsData['entry'];
}
const CategoryGroupGiftList = t0 => {
  const $ = _c(15);
  const {
    groupCode,
    sortType,
    initialGiftsResponse,
    entry
  } = t0;
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const isInitialQuery = groupCode === ALL_CATEGORY_GROUP_CODE && sortType === GIFT_PRODUCT_SORT_TYPE.RECOMMEND;
  let t1;
  if ($[0] !== entry || $[1] !== groupCode || $[2] !== sortType) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/home-love/main/category-group-gift-products"]({
      query: {
        giftCategoryGroupType: groupCode,
        giftProductSortType: sortType,
        page: 1,
        size: GIFT_PAGE_SIZE,
        entry
      }
    });
    $[0] = entry;
    $[1] = groupCode;
    $[2] = sortType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = isInitialQuery ? initialGiftsResponse : undefined;
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      ...t1,
      select: _temp,
      initialData: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: giftList
  } = useSuspenseQuery(t3);
  if (!giftList.length) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ul className={GIFT_LIST_CLASS_NAME}><li className="col-span-full h-92 text-Gray-600 flex-center typo-body3">조회된 답례품이 없어요.</li></ul>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== giftList || $[9] !== isDesktop) {
    let t5;
    if ($[11] !== isDesktop) {
      t5 = (giftData, index) => <li key={giftData.productNumber} className={cn(index >= MOBILE_GIFT_COUNT && "mobile:hidden")}><GiftCard mainImageUrl={giftData.mainImageUrl} productName={giftData.productName} donationAmount={giftData.requiredDonationAmount} reviewCount={giftData.reviewCount} reviewRating={giftData.reviewRating} ratingSize={isDesktop ? 16 : 12} institutionName={giftData.institutionName} productNumber={giftData.productSlugId ?? giftData.productNumber} isNew={giftData.newDisplayYN} priority={index < 4} isAdult={giftData.adultYN} isSoldOut={giftData.productSaleStatus === GIFT_SALE_STATUS.SOLD_OUT} /></li>;
      $[11] = isDesktop;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = giftList.map(t5);
    $[8] = giftList;
    $[9] = isDesktop;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <ul className={GIFT_LIST_CLASS_NAME}>{t4}</ul>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export const CategoryGroupGiftsSection = t0 => {
  const $ = _c(54);
  const {
    className,
    categoryGroups,
    initialGiftsResponse,
    entry
  } = t0;
  const {
    reset
  } = useQueryErrorResetBoundary();
  const [selectedGroupCode, setSelectedGroupCode] = useState(ALL_CATEGORY_GROUP_CODE);
  const [selectedSortType, setSelectedSortType] = useState(GIFT_PRODUCT_SORT_TYPE.RECOMMEND);
  let t1;
  let t2;
  if ($[0] !== categoryGroups) {
    t1 = () => {
      try {
        const savedGroupCode = sessionStorage.getItem(STORAGE_KEY.DONATION_HOME_SELECTED_GIFT_CATEGORY_GROUP);
        const savedGroup = categoryGroups.find(group => group.groupCode === savedGroupCode);
        if (!savedGroup) {
          return;
        }
        setSelectedGroupCode(savedGroup.groupCode);
      } catch {}
    };
    t2 = [categoryGroups];
    $[0] = categoryGroups;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const isAllGroup = selectedGroupCode === ALL_CATEGORY_GROUP_CODE;
  const deferredGroupCode = useDeferredValue(selectedGroupCode);
  const deferredSortType = useDeferredValue(selectedSortType);
  const isSwitching = deferredGroupCode !== selectedGroupCode || deferredSortType !== selectedSortType;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = groupCode => {
      setSelectedGroupCode(groupCode);
      try {
        if (groupCode === ALL_CATEGORY_GROUP_CODE) {
          sessionStorage.removeItem(STORAGE_KEY.DONATION_HOME_SELECTED_GIFT_CATEGORY_GROUP);
          return;
        }
        sessionStorage.setItem(STORAGE_KEY.DONATION_HOME_SELECTED_GIFT_CATEGORY_GROUP, groupCode);
      } catch {}
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleClickGroup = t3;
  let t4;
  if ($[4] !== categoryGroups || $[5] !== selectedGroupCode) {
    let t5;
    if ($[7] !== selectedGroupCode) {
      t5 = t6 => {
        const {
          groupCode: groupCode_0,
          groupName,
          imageUrl
        } = t6;
        return <div key={groupCode_0} className="flex-[0_0_auto]"><CategoryGroupItem imageUrl={imageUrl} groupName={groupName} active={selectedGroupCode === groupCode_0} onClick={() => handleClickGroup(groupCode_0)} /></div>;
      };
      $[7] = selectedGroupCode;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = categoryGroups.map(t5);
    $[4] = categoryGroups;
    $[5] = selectedGroupCode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const groupItems = t4;
  let t5;
  if ($[9] !== className) {
    t5 = cn("flex flex-col items-center px-40 content-container", "mobile-tablet:px-20", className);
    $[9] = className;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className={cn("pb-24 text-center font-700 text-Gray-900 typo-h4", "mobile-tablet:w-full mobile-tablet:pb-16 mobile-tablet:text-left mobile-tablet:typo-body1")}>지금 인기 있는 답례품</h2>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== categoryGroups.length || $[13] !== groupItems) {
    t7 = categoryGroups.length > 0 && <><Responsive.MobileOnly display="block" className="w-full pb-12"><HorizontalScroll className="-mx-20" slideClassName="gap-8 pl-20">{groupItems}<span aria-hidden="true" className="w-20 shrink-0" /></HorizontalScroll></Responsive.MobileOnly><Responsive.DesktopOnly display="block" className="w-full"><CategoryGroupRows items={groupItems} className="flex flex-col gap-8 pb-32" rowClassName="flex flex-wrap justify-center gap-x-16 gap-y-8" /></Responsive.DesktopOnly></>;
    $[12] = categoryGroups.length;
    $[13] = groupItems;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("flex items-center gap-6 pb-24", "mobile-tablet:hidden");
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== selectedSortType) {
    t9 = SORT_TYPE_OPTIONS.map(t10 => {
      const {
        label,
        value
      } = t10;
      return <li key={value}><Chip checked={selectedSortType === value} onClick={() => setSelectedSortType(value)}>{label}</Chip></li>;
    });
    $[16] = selectedSortType;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <ul className={t8}>{t9}</ul>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7) {
    t11 = <header className="flex w-full flex-col items-center">{t6}{t7}{t10}</header>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== deferredGroupCode || $[24] !== deferredSortType) {
    t12 = [deferredGroupCode, deferredSortType];
    $[23] = deferredGroupCode;
    $[24] = deferredSortType;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ul className={GIFT_LIST_CLASS_NAME}>{Array.from({
        length: MOBILE_GIFT_COUNT
      }, _temp3)}</ul>;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = isSwitching && "opacity-60";
  let t15;
  if ($[27] !== t14) {
    t15 = cn("w-full transition-opacity duration-200", t14);
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== deferredGroupCode || $[30] !== deferredSortType || $[31] !== entry || $[32] !== initialGiftsResponse) {
    t16 = <CategoryGroupGiftList groupCode={deferredGroupCode} sortType={deferredSortType} initialGiftsResponse={initialGiftsResponse} entry={entry} />;
    $[29] = deferredGroupCode;
    $[30] = deferredSortType;
    $[31] = entry;
    $[32] = initialGiftsResponse;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== isSwitching || $[35] !== t15 || $[36] !== t16) {
    t17 = <Suspense fallback={t13}><div aria-busy={isSwitching} className={t15}>{t16}</div></Suspense>;
    $[34] = isSwitching;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== reset || $[39] !== t12 || $[40] !== t17) {
    t18 = <ErrorBoundary resetKeys={t12} onReset={reset} fallbackRender={_temp2}>{t17}</ErrorBoundary>;
    $[38] = reset;
    $[39] = t12;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== isAllGroup || $[43] !== selectedGroupCode || $[44] !== selectedSortType) {
    t19 = DONATION_ROUTES.GIFTS.withSearchParams({
      searchParams: {
        sort: selectedSortType,
        group: isAllGroup ? undefined : selectedGroupCode
      }
    });
    $[42] = isAllGroup;
    $[43] = selectedGroupCode;
    $[44] = selectedSortType;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <SvgChevronright className="size-16 text-Gray-black transition-transform duration-150 group-hover:translate-x-1 group-focus-visible:translate-x-1 motion-reduce:transition-none" />;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t19) {
    t21 = <Button theme="sub-fill" dimensions="H40" asChild={true}><CustomLink href={t19} aria-label={"\uC804\uCCB4 \uB2F5\uB840\uD488 \uBAA9\uB85D \uD398\uC774\uC9C0\uB85C \uC774\uB3D9"} className="group flex w-[30rem] max-w-full items-center gap-4">답례품 전체보기{t20}</CustomLink></Button>;
    $[47] = t19;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t11 || $[50] !== t18 || $[51] !== t21 || $[52] !== t5) {
    t22 = <section className={t5}>{t11}{t18}{t21}</section>;
    $[49] = t11;
    $[50] = t18;
    $[51] = t21;
    $[52] = t5;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  return t22;
};
function _temp(data) {
  return data.context?.contents ?? [];
}
function _temp2(t0) {
  const {
    resetErrorBoundary
  } = t0;
  return <div className="flex w-full flex-col items-center gap-16 py-40 pb-60 mobile-tablet:pb-40"><p className="text-center text-Gray-600 typo-body3">답례품을 불러오지 못했어요.<br />잠시 후 다시 시도해 주세요.</p><Button theme="primary-line" dimensions="H40" onClick={resetErrorBoundary}>다시 시도</Button></div>;
}
function _temp3(_, index) {
  return <li key={index}><GiftCard isLoading={true} /></li>;
}